import { VolRangeData } from '../../../../../providers/widgets/volatility-range-provider/types';

import { useMemo } from 'react';

const getBaseSymbol = (symbol: string) => symbol.replace(/_USDC$/, '');

const colors = {
  range: '#9E77ED',
  current: '#26D9D9',
  max: '#FAA638',
  min: '#ABBD75',
};

export const useProcessVolatilityRangeData = (rawData: VolRangeData) => {
  return useMemo(() => {
    let xAxis = {
      type: 'category',
      data: <string[]>[],
    };

    const yAxis = {
      type: 'value',
      axisLabel: {
        formatter: (value: any) => `${(value * 100).toFixed(0)}%`,
      },
    };

    if (!Array.isArray(rawData) || rawData.length === 0) {
      return { series: [], xAxis, yAxis };
    }

    const symbolOrder = ['BTC', 'ETH', 'SOL', 'XRP'];

    const filteredData = rawData.filter((d) => {
      const base = getBaseSymbol(d.symbol);
      return base !== 'PAXG';
    });

    const orderedData = [...filteredData].sort((a, b) => {
      const aBase = getBaseSymbol(a.symbol);
      const bBase = getBaseSymbol(b.symbol);
      const aIdx = symbolOrder.indexOf(aBase);
      const bIdx = symbolOrder.indexOf(bBase);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return aBase.localeCompare(bBase);
    });

    const symbols = orderedData.map((d) => getBaseSymbol(d.symbol));

    const p25p75Series = {
      type: 'custom',
      name: 'Range (P25-P75)',
      renderItem: function (_: any, api: any) {
        const categoryIndex = api.value(0);
        const lowPoint = api.coord([categoryIndex, api.value(1)]);
        const highPoint = api.coord([categoryIndex, api.value(2)]);
        return {
          type: 'line',
          shape: {
            x1: lowPoint[0],
            y1: lowPoint[1],
            x2: highPoint[0],
            y2: highPoint[1],
          },
          style: {
            stroke: colors.range,
            lineWidth: 4,
            cap: 'round',
          },
        };
      },
      encode: { x: 0, y: [1, 2] },
      data: orderedData.map((d, i) => [i, d.p25, d.p75]),
      tooltip: {
        formatter: (params: any) => {
          const symbol = symbols[params.data[0]];
          const p25 = (params.data[1] * 100).toFixed(2);
          const p75 = (params.data[2] * 100).toFixed(2);
          return `${symbol}<br/>Range: ${p25}% - ${p75}%`;
        },
      },
    };

    const currentSeries = {
      name: 'Current',
      type: 'scatter',
      symbolSize: 12,
      itemStyle: { color: colors.current },
      data: orderedData.map((d, i) => [i, d.current]),
    };

    const maxSeries = {
      name: 'Max',
      type: 'scatter',
      symbolSize: 10,
      itemStyle: { color: colors.max },
      data: orderedData.map((d, i) => [i, d.high]),
    };

    const minSeries = {
      name: 'Min',
      type: 'scatter',
      symbolSize: 10,
      itemStyle: { color: colors.min },
      data: orderedData.map((d, i) => [i, d.low]),
    };

    xAxis = {
      ...xAxis,
      data: symbols,
    };

    return {
      series: [p25p75Series, currentSeries, maxSeries, minSeries],
      xAxis,
      yAxis,
    };
  }, [rawData]);
};
